import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Alert,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useUser } from './userContext';
import { useThemeContext, THEME_CLASSIC } from '../theme/ThemeContext';

/**
 * MarkdownViewerDialog - Dialog to display markdown content from doc/100-user-hilfen
 *
 * Props:
 * - open: boolean - whether dialog is open
 * - onClose: function - callback when dialog should close
 * - filePath: string - path to markdown file (e.g., "doc/100-user-hilfen/transport-eingeben.md")
 * - title: string - optional title for dialog (defaults to filename)
 */
const MarkdownViewerDialog = ({ open, onClose, filePath, title }) => {
    const { user } = useUser();
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [content, setContent] = useState('');
    const [displayTitle, setDisplayTitle] = useState(title || '');

    useEffect(() => {
        if (!open || !filePath) {
            return;
        }

        const fetchMarkdown = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(
                    `${process.env.REACT_APP_API_URI}/api/docs/markdown?path=${encodeURIComponent(filePath)}`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': user.api_key,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.success && data.data) {
                    setContent(data.data.content);
                    setDisplayTitle(title || data.data.title || filePath.split('/').pop());
                } else {
                    throw new Error('Invalid response format');
                }
            } catch (err) {
                console.error('Error loading markdown:', err);
                setError(err.message || 'Fehler beim Laden der Markdown-Datei');
            } finally {
                setLoading(false);
            }
        };

        fetchMarkdown();
    }, [open, filePath, title, user.api_key]);

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: isClassic ? '#ffffff' : '#1e1e1e',
                    color: isClassic ? '#000000' : '#e0e0e0',
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isClassic ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)',
                    pb: 2
                }}
            >
                <Box sx={{ fontWeight: 600 }}>
                    {displayTitle}
                </Box>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: isClassic ? 'rgba(0,0,0,0.54)' : 'rgba(255,255,255,0.7)'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    mt: 2,
                    minHeight: '200px'
                }}
            >
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && content && (
                    <Box
                        sx={{
                            '& h1': { fontSize: '1.8rem', fontWeight: 600, mb: 2, mt: 3 },
                            '& h2': { fontSize: '1.5rem', fontWeight: 600, mb: 1.5, mt: 2.5 },
                            '& h3': { fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 2 },
                            '& p': { mb: 1.5, lineHeight: 1.7 },
                            '& ul, & ol': { mb: 1.5, pl: 3 },
                            '& li': { mb: 0.5 },
                            '& code': {
                                bgcolor: isClassic ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '0.9em'
                            },
                            '& pre': {
                                bgcolor: isClassic ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                padding: '12px',
                                borderRadius: '4px',
                                overflow: 'auto',
                                mb: 2
                            },
                            '& pre code': {
                                bgcolor: 'transparent',
                                padding: 0
                            },
                            '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                mb: 2
                            },
                            '& th, & td': {
                                border: isClassic ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)',
                                padding: '8px 12px',
                                textAlign: 'left'
                            },
                            '& th': {
                                bgcolor: isClassic ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
                                fontWeight: 600
                            },
                            '& blockquote': {
                                borderLeft: isClassic ? '4px solid rgba(0,0,0,0.2)' : '4px solid rgba(255,255,255,0.2)',
                                pl: 2,
                                ml: 0,
                                fontStyle: 'italic',
                                color: isClassic ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)'
                            },
                            '& a': {
                                color: isClassic ? '#1976d2' : '#90caf9',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }
                        }}
                    >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {content}
                        </ReactMarkdown>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: isClassic ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)',
                    pt: 2
                }}
            >
                <Button onClick={handleClose} variant="contained">
                    Schließen
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkdownViewerDialog;
